import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Copy, Mail, Trash2, UserPlus } from 'lucide-react';
import { getSupabaseClient, hasSupabaseConfig } from '../lib/supabaseClient';
import { formatSupabaseError } from '../lib/supabaseErrors';
import { useAuth } from '../auth/AuthContext';
import { useModuleAccess } from '../auth/useModuleAccess';
import {
  MODULE_KEYS,
  ROLES,
  defaultModulesForRole,
  normalizeModules,
  normalizeRole,
} from '../auth/moduleAccess';
import ContextHelpTip from '../components/common/ContextHelpTip';
import SignedInUserLine from '../components/common/SignedInUserLine';

const ROLE_HELP_TEXT = (
  <div className="space-y-2 text-left">
    <p className="font-semibold text-pp-ink">Roles set default access</p>
    <p>
      <span className="font-medium">Admin</span> — Full access, including inviting
      users, Users admin, Member roster, and Organization settings.
    </p>
    <p>
      <span className="font-medium">Chair</span> — Meetings, committees, and
      elections (typical president / chairperson). No admin or org settings.
    </p>
    <p>
      <span className="font-medium">Secretary</span> — Meetings and committees.
      No elections or admin by default.
    </p>
    <p>
      <span className="font-medium">Committee</span> — Committees only. Can work
      assigned referrals and mini-meetings; does not run the meeting console unless
      you add the meeting module.
    </p>
    <p className="text-pp-muted">
      You can adjust the module checkboxes for anyone except Admins.
    </p>
  </div>
);

export default function AdminPage() {
  const { orgId } = useParams();
  const navigate = useNavigate();
  const { switchOrganization, ready, memberships } = useAuth();
  const { canAdmin } = useModuleAccess();
  const [members, setMembers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('Chair');
  const [inviteModules, setInviteModules] = useState(defaultModulesForRole('Chair'));
  const [lastInviteLink, setLastInviteLink] = useState(null);
  const [lastInviteNote, setLastInviteNote] = useState(null);
  const [editingId, setEditingId] = useState(null);

  const membershipForOrg = memberships.find((m) => m.organization_id === orgId);

  useEffect(() => {
    if (!ready) return;
    if (!hasSupabaseConfig()) {
      navigate('/', { replace: true });
      return;
    }
    if (orgId) void switchOrganization(orgId);
  }, [ready, orgId, switchOrganization, navigate]);

  useEffect(() => {
    if (ready && orgId && membershipForOrg && !canAdmin) {
      navigate(`/orgs/${orgId}`, { replace: true });
    }
  }, [ready, orgId, canAdmin, membershipForOrg, navigate]);

  const load = useCallback(async () => {
    const supabase = getSupabaseClient();
    if (!supabase || !orgId) return;
    setError(null);
    try {
      const { data: list, error: listErr } = await supabase.rpc('list_org_memberships', {
        p_organization_id: orgId,
      });
      if (listErr) throw listErr;
      setMembers(
        (list ?? []).map((row) => {
          const role = normalizeRole(row.role);
          return {
            ...row,
            role,
            modules: normalizeModules(role, row.modules),
          };
        })
      );

      const { data: inv, error: invErr } = await supabase.rpc('list_pending_org_invitations', {
        p_organization_id: orgId,
      });
      if (invErr) throw invErr;
      setInvites(inv ?? []);
    } catch (err) {
      console.error('[Admin] load failed', err);
      setError(formatSupabaseError(err, 'Failed to load admin data'));
    }
  }, [orgId]);

  useEffect(() => {
    if (canAdmin && orgId && membershipForOrg) void load();
  }, [canAdmin, orgId, membershipForOrg, load]);

  const onRoleChangeForInvite = (role) => {
    setInviteRole(role);
    setInviteModules(defaultModulesForRole(role));
  };

  const inviteUser = async (e) => {
    e.preventDefault();
    const supabase = getSupabaseClient();
    if (!supabase) return;
    setBusy(true);
    setError(null);
    setLastInviteLink(null);
    setLastInviteNote(null);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const accessToken = sessionData.session?.access_token;
      if (!accessToken) throw new Error('Not signed in');

      const functionsUrl = `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/invite-user`;
      const res = await fetch(functionsUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
          apikey: process.env.REACT_APP_SUPABASE_ANON_KEY,
        },
        body: JSON.stringify({
          organization_id: orgId,
          email: inviteEmail.trim(),
          role: inviteRole,
          modules: normalizeModules(inviteRole, inviteModules),
          app_origin: window.location.origin,
        }),
      });

      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        if (res.status === 404 || res.status === 0) {
          const { data: token, error: rpcErr } = await supabase.rpc('create_org_invitation', {
            p_organization_id: orgId,
            p_email: inviteEmail.trim(),
            p_role: inviteRole,
            p_modules: normalizeModules(inviteRole, inviteModules),
          });
          if (rpcErr) throw rpcErr;
          const link = `${window.location.origin}/join/${token}`;
          setLastInviteLink(link);
          setLastInviteNote(
            'Invite link created, but the invite-user Edge Function is not deployed — no Auth user yet. The invitee must open this link and create an account (they will appear under Authentication → Users after signup).'
          );
          setInviteEmail('');
          await load();
          return;
        }
        throw new Error(body.error || body.message || `Invite failed (${res.status})`);
      }

      const token = body.token;
      const link = body.invite_link || `${window.location.origin}/join/${token}`;
      setLastInviteLink(link);
      if (body.email_sent === false) {
        setLastInviteNote(
          body.warning
            ? `Auth invite did not send email (${body.warning}). Use the join link; invitee may already exist or must create an account.`
            : 'Join link ready. Auth email was not sent — have the invitee open the link and sign in or create an account.'
        );
      } else {
        setLastInviteNote(
          'Auth invite sent (if SMTP delivered). Invitee should also appear under Authentication → Users. They can use this join link if the email never arrives.'
        );
      }
      setInviteEmail('');
      await load();
    } catch (err) {
      try {
        const supabaseClient = getSupabaseClient();
        const { data: token, error: rpcErr } = await supabaseClient.rpc('create_org_invitation', {
          p_organization_id: orgId,
          p_email: inviteEmail.trim(),
          p_role: inviteRole,
          p_modules: normalizeModules(inviteRole, inviteModules),
        });
        if (rpcErr) throw rpcErr;
        const link = `${window.location.origin}/join/${token}`;
        setLastInviteLink(link);
        setLastInviteNote(
          'Invite link created via fallback (Edge Function unavailable). No Auth user yet — invitee opens the link and creates an account with the invited email.'
        );
        setInviteEmail('');
        await load();
      } catch (fallbackErr) {
        setError(
          `${formatSupabaseError(err, 'Invite failed')} / ${formatSupabaseError(
            fallbackErr,
            'fallback failed'
          )}`
        );
      }
    } finally {
      setBusy(false);
    }
  };

  const saveMembership = async (row) => {
    const supabase = getSupabaseClient();
    setBusy(true);
    setError(null);
    try {
      const { error: rpcErr } = await supabase.rpc('admin_update_membership', {
        p_user_id: row.user_id,
        p_organization_id: orgId,
        p_role: row.role,
        p_modules: normalizeModules(row.role, row.modules),
        p_is_active: row.is_active,
      });
      if (rpcErr) throw rpcErr;
      setEditingId(null);
      await load();
    } catch (err) {
      setError(formatSupabaseError(err, 'Update failed'));
    } finally {
      setBusy(false);
    }
  };

  const removeMembership = async (userId) => {
    if (!window.confirm('Deactivate this user for the organization?')) return;
    const supabase = getSupabaseClient();
    setBusy(true);
    setError(null);
    try {
      const { error: rpcErr } = await supabase.rpc('admin_remove_membership', {
        p_user_id: userId,
        p_organization_id: orgId,
      });
      if (rpcErr) throw rpcErr;
      await load();
    } catch (err) {
      setError(formatSupabaseError(err, 'Remove failed'));
    } finally {
      setBusy(false);
    }
  };

  const revokeInvite = async (invitationId) => {
    const supabase = getSupabaseClient();
    setBusy(true);
    try {
      const { error: rpcErr } = await supabase.rpc('admin_revoke_invitation', {
        p_invitation_id: invitationId,
      });
      if (rpcErr) throw rpcErr;
      await load();
    } catch (err) {
      setError(formatSupabaseError(err, 'Revoke failed'));
    } finally {
      setBusy(false);
    }
  };

  const orgName = membershipForOrg?.organization_name || 'Organization';

  const copyLink = async () => {
    if (!lastInviteLink) return;
    try {
      await navigator.clipboard.writeText(lastInviteLink);
    } catch {
      /* ignore */
    }
  };

  return (
    <div className="min-h-screen bg-pp-paper text-pp-ink">
      <header className="border-b border-pp-line/80 bg-pp-surface">
        <div className="mx-auto flex max-w-4xl items-center justify-between gap-4 px-4 py-4 sm:px-6">
          <div>
            <Link
              to={`/orgs/${orgId}`}
              className="inline-flex items-center gap-1 text-xs font-medium text-pp-muted hover:text-pp-ink"
            >
              <ArrowLeft className="h-3.5 w-3.5" />
              Back to {orgName}
            </Link>
            <div className="mt-0.5 flex items-center gap-1.5">
              <h1 className="text-xl font-semibold tracking-tight">Users</h1>
              <ContextHelpTip text={ROLE_HELP_TEXT} />
            </div>
            <p className="text-sm text-pp-muted">Invite logins and manage module access</p>
            <SignedInUserLine className="mt-1" />
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Link
              to={`/orgs/${orgId}/roster`}
              className="rounded-xl border border-pp-line px-3 py-2 text-sm font-medium text-pp-muted hover:bg-pp-paper"
            >
              Member roster
            </Link>
            <Link
              to={`/orgs/${orgId}/settings`}
              className="rounded-xl border border-pp-line px-3 py-2 text-sm font-medium text-pp-muted hover:bg-pp-paper"
            >
              Org settings
            </Link>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-4xl space-y-8 px-4 py-8 sm:px-6">
        {error && (
          <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
            {error}
          </div>
        )}

        <section className="rounded-2xl border border-pp-line bg-pp-surface p-6 shadow-pp">
          <h2 className="flex items-center gap-2 text-base font-semibold">
            <UserPlus className="h-4 w-4 text-pp-accent" />
            Invite user
          </h2>
          <form className="mt-4 grid gap-3 sm:grid-cols-2" onSubmit={inviteUser}>
            <div className="sm:col-span-2">
              <label className="text-xs font-medium text-pp-muted">Email</label>
              <input
                type="email"
                required
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                className="mt-1 w-full rounded-lg border border-pp-line bg-white px-3 py-2 text-sm"
              />
            </div>
            <div>
              <div className="flex items-center gap-1">
                <label className="text-xs font-medium text-pp-muted">Role</label>
                <ContextHelpTip text={ROLE_HELP_TEXT} iconClassName="h-3.5 w-3.5 shrink-0" />
              </div>
              <select
                value={inviteRole}
                onChange={(e) => onRoleChangeForInvite(e.target.value)}
                className="mt-1 w-full rounded-lg border border-pp-line bg-white px-3 py-2 text-sm"
              >
                {ROLES.map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-pp-muted">Modules</label>
              <div className="mt-2 flex flex-wrap gap-3">
                {MODULE_KEYS.map((key) => (
                  <label key={key} className="inline-flex items-center gap-1.5 text-xs capitalize">
                    <input
                      type="checkbox"
                      checked={Boolean(inviteModules[key])}
                      disabled={inviteRole === 'Admin' || key === 'admin'}
                      onChange={(e) =>
                        setInviteModules((prev) => ({ ...prev, [key]: e.target.checked }))
                      }
                    />
                    {key}
                  </label>
                ))}
              </div>
            </div>
            <div className="sm:col-span-2">
              <button
                type="submit"
                disabled={busy}
                className="rounded-xl bg-pp-accent px-4 py-2.5 text-sm font-semibold text-white disabled:opacity-50"
              >
                {busy ? 'Sending…' : 'Send invite'}
              </button>
            </div>
          </form>
          {lastInviteLink && (
            <div className="mt-4 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm">
              <p className="font-medium text-emerald-800">Invite link (copy if email is delayed)</p>
              <div className="mt-2 flex items-center gap-2">
                <code className="flex-1 truncate text-xs text-emerald-900">{lastInviteLink}</code>
                <button
                  type="button"
                  onClick={() => void copyLink()}
                  className="inline-flex items-center gap-1 rounded-lg border border-emerald-300 px-2 py-1 text-xs font-medium text-emerald-800"
                >
                  <Copy className="h-3.5 w-3.5" />
                  Copy
                </button>
              </div>
              {lastInviteNote && (
                <p className="mt-2 text-xs text-emerald-900/90">{lastInviteNote}</p>
              )}
            </div>
          )}
        </section>

        <section className="rounded-2xl border border-pp-line bg-pp-surface p-6 shadow-pp">
          <h2 className="text-base font-semibold">Users</h2>
          <ul className="mt-4 divide-y divide-pp-line">
            {members.map((row) => {
              const editing = editingId === row.user_id;
              return (
                <li key={row.membership_id} className="py-4">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <p className="font-medium">{row.display_name || row.email || 'User'}</p>
                      <p className="text-xs text-pp-muted">{row.email}</p>
                      {!row.is_active && (
                        <span className="mt-1 inline-block text-xs font-medium text-rose-600">
                          Inactive
                        </span>
                      )}
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      {editing ? (
                        <>
                          <select
                            value={row.role}
                            onChange={(e) => {
                              const role = e.target.value;
                              setMembers((prev) =>
                                prev.map((m) =>
                                  m.user_id === row.user_id
                                    ? {
                                        ...m,
                                        role,
                                        modules: defaultModulesForRole(role),
                                      }
                                    : m
                                )
                              );
                            }}
                            className="rounded-lg border border-pp-line px-2 py-1 text-sm"
                          >
                            {ROLES.map((r) => (
                              <option key={r} value={r}>
                                {r}
                              </option>
                            ))}
                          </select>
                          {MODULE_KEYS.map((key) => (
                            <label key={key} className="inline-flex items-center gap-1 text-xs capitalize">
                              <input
                                type="checkbox"
                                checked={Boolean(row.modules?.[key])}
                                disabled={row.role === 'Admin'}
                                onChange={(e) => {
                                  setMembers((prev) =>
                                    prev.map((m) =>
                                      m.user_id === row.user_id
                                        ? {
                                            ...m,
                                            modules: { ...m.modules, [key]: e.target.checked },
                                          }
                                        : m
                                    )
                                  );
                                }}
                              />
                              {key}
                            </label>
                          ))}
                          <button
                            type="button"
                            disabled={busy}
                            onClick={() => void saveMembership(row)}
                            className="rounded-lg bg-pp-ink px-3 py-1.5 text-xs font-medium text-white"
                          >
                            Save
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setEditingId(null);
                              void load();
                            }}
                            className="rounded-lg border border-pp-line px-3 py-1.5 text-xs"
                          >
                            Cancel
                          </button>
                        </>
                      ) : (
                        <>
                          <span className="rounded-full bg-pp-paper px-2.5 py-1 text-xs font-medium">
                            {row.role}
                          </span>
                          <button
                            type="button"
                            onClick={() => setEditingId(row.user_id)}
                            className="rounded-lg border border-pp-line px-3 py-1.5 text-xs font-medium"
                          >
                            Edit
                          </button>
                          {row.is_active && (
                            <button
                              type="button"
                              onClick={() => void removeMembership(row.user_id)}
                              className="inline-flex items-center gap-1 rounded-lg border border-rose-200 px-3 py-1.5 text-xs font-medium text-rose-700"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                              Deactivate
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                  {!editing && (
                    <p className="mt-2 text-xs text-pp-muted">
                      Modules:{' '}
                      {MODULE_KEYS.filter((k) => row.modules?.[k]).join(', ') || 'none'}
                    </p>
                  )}
                </li>
              );
            })}
            {members.length === 0 && (
              <li className="py-4 text-sm text-pp-muted">No members found.</li>
            )}
          </ul>
        </section>

        <section className="rounded-2xl border border-pp-line bg-pp-surface p-6 shadow-pp">
          <h2 className="flex items-center gap-2 text-base font-semibold">
            <Mail className="h-4 w-4 text-pp-accent" />
            Pending invitations
          </h2>
          <ul className="mt-4 divide-y divide-pp-line">
            {invites.map((inv) => (
              <li key={inv.id} className="flex flex-wrap items-center justify-between gap-2 py-3">
                <div>
                  <p className="text-sm font-medium">{inv.email}</p>
                  <p className="text-xs text-pp-muted">
                    {inv.role} · expires {new Date(inv.expires_at).toLocaleDateString()}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      const link = `${window.location.origin}/join/${inv.token}`;
                      setLastInviteLink(link);
                      void navigator.clipboard?.writeText(link);
                    }}
                    className="rounded-lg border border-pp-line px-3 py-1.5 text-xs font-medium"
                  >
                    Copy link
                  </button>
                  <button
                    type="button"
                    onClick={() => void revokeInvite(inv.id)}
                    className="rounded-lg border border-rose-200 px-3 py-1.5 text-xs font-medium text-rose-700"
                  >
                    Revoke
                  </button>
                </div>
              </li>
            ))}
            {invites.length === 0 && (
              <li className="py-3 text-sm text-pp-muted">No pending invitations.</li>
            )}
          </ul>
        </section>
      </main>
    </div>
  );
}
